import { useState } from "react"
import styled from "styled-components"

import { useFetchOrganization } from "src/data/organizations/queries/organizationQueries"
import { useFetchParadiseHome } from "src/data/paradise/paradiseHomes/queries/paradiseHomeQueries"
import { useFetchParadiseUser } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { MLocation, TPartialLocation } from "src/router/routeTypes"
import { CopyText } from "src/ui/CopyText/CopyText"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MText, MTextProps } from "src/ui/MText"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

export function TruncatedHomeId({
  homeId,
  to,
  textVariant,
}: {
  homeId: string | undefined
  to?: TPartialLocation & Partial<MLocation>
  textVariant?: MTextProps["variant"]
}) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const useFetchHomeQuery = useFetchParadiseHome({
    homeId: homeId ?? "",
    options: {
      enabled: isTooltipOpen,
    },
  })

  return (
    <TruncatedEntityId
      entityId={homeId}
      isTooltipOpen={isTooltipOpen}
      setIsTooltipOpen={setIsTooltipOpen}
      entityName={useFetchHomeQuery.data?.name}
      to={to}
      textVariant={textVariant}
    />
  )
}

export function TruncatedUserId({
  userId,
  to,
  textVariant,
}: {
  userId: string | undefined
  to?: TPartialLocation & Partial<MLocation>
  textVariant?: MTextProps["variant"]
}) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const useFetchUserQuery = useFetchParadiseUser({
    userId: userId ?? "",
    options: {
      enabled: isTooltipOpen,
    },
  })

  return (
    <TruncatedEntityId
      entityId={userId}
      isTooltipOpen={isTooltipOpen}
      setIsTooltipOpen={setIsTooltipOpen}
      entityName={useFetchUserQuery.data?.email}
      to={to}
      textVariant={textVariant}
    />
  )
}

export function TruncatedOrganizationId({
  organizationId,
  to,
  textVariant,
}: {
  organizationId: string | undefined
  to?: TPartialLocation & Partial<MLocation>
  textVariant?: MTextProps["variant"]
}) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const useFetchOrganizationQuery = useFetchOrganization({
    id: organizationId ?? "",
    options: {
      enabled: isTooltipOpen,
    },
  })
  return (
    <TruncatedEntityId
      entityId={organizationId}
      isTooltipOpen={isTooltipOpen}
      setIsTooltipOpen={setIsTooltipOpen}
      entityName={useFetchOrganizationQuery.data?.name}
      to={to}
      textVariant={textVariant}
    />
  )
}
export function TruncatedEntityId({
  entityId,
  to,
  isTooltipOpen,
  setIsTooltipOpen,
  entityName,
  textVariant,
}: {
  entityId: string | undefined
  isTooltipOpen?: boolean
  setIsTooltipOpen?: (isTooltipOpen: boolean) => void
  to?: TPartialLocation & Partial<MLocation>
  entityName?: string
  textVariant?: MTextProps["variant"]
}) {
  const [internalOpen, setInternalOpen] = useState(false)

  const _open = isTooltipOpen === undefined ? internalOpen : isTooltipOpen
  const _setIsOpen =
    setIsTooltipOpen === undefined ? setInternalOpen : setIsTooltipOpen

  function truncateId(id: string | undefined) {
    if (!id) return ""
    return "..." + id.substring(18, 24)
  }

  if (entityId === undefined) return null

  const tooltipContent = (
    <MText variant="bodyS">
      {to !== undefined ? (
        <ToolTipInnerWrapper>
          <MText variant="bodyS">{entityId}</MText>
          <MText variant="bodyS">{entityName}</MText>
        </ToolTipInnerWrapper>
      ) : (
        <ToolTipInnerWrapper>
          <MText variant="bodyS">{entityId}</MText>
          <MText variant="bodyS">Click to copy</MText>
        </ToolTipInnerWrapper>
      )}
    </MText>
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <MTooltip open={_open} title={tooltipContent}>
        <div
          onMouseEnter={() => _setIsOpen(true)}
          onMouseLeave={() => _setIsOpen(false)}
        >
          {to !== undefined ? (
            <MText variant={textVariant}>
              <InternalLink to={to}>{truncateId(entityId)}</InternalLink>
            </MText>
          ) : (
            <CopyText
              value={entityId}
              copiedText="Copied!"
              copiedTextTimeoutMs={1000}
              button={
                <MText style={{ cursor: "pointer" }} variant={textVariant}>
                  {truncateId(entityId)}
                </MText>
              }
            />
          )}
        </div>
      </MTooltip>
    </div>
  )
}

const ToolTipInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
